import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './AdminBookings.css';

const AdminBookings = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState(''); // 'confirm', 'reschedule', 'cancel'
  const [formData, setFormData] = useState({
    meetingLink: '',
    adminNotes: '',
    newDate: '',
    newTime: '',
    reason: ''
  });

  useEffect(() => {
    fetchBookings();
  }, [filter]);

  const fetchBookings = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');
      const params = filter !== 'all' ? { status: filter } : {};
      
      const response = await axios.get('/api/admin/bookings', {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      
      setBookings(response.data.bookings);
    } catch (error) {
      console.error('Error fetching bookings:', error);
      alert('Failed to load bookings');
    } finally {
      setLoading(false);
    }
  };

  const openModal = (type, booking) => {
    setModalType(type);
    setSelectedBooking(booking);
    setFormData({
      meetingLink: booking.meetingLink || '',
      adminNotes: booking.adminResponse?.adminNotes || '',
      newDate: '',
      newTime: '',
      reason: ''
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedBooking(null);
    setModalType('');
    setFormData({
      meetingLink: '',
      adminNotes: '',
      newDate: '',
      newTime: '',
      reason: ''
    });
  };

  const handleConfirm = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      
      await axios.post(
        `/api/admin/bookings/${selectedBooking._id}/confirm`,
        {
          meetingLink: formData.meetingLink,
          adminNotes: formData.adminNotes
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      alert('Booking confirmed! Email sent to client.');
      closeModal();
      fetchBookings();
    } catch (error) {
      console.error('Error confirming booking:', error);
      alert('Failed to confirm booking');
    }
  };

  const handleReschedule = async () => {
    if (!formData.newDate || !formData.newTime) {
      alert('Please select both date and time');
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      
      await axios.post(
        `/api/admin/bookings/${selectedBooking._id}/reschedule`,
        {
          newDate: formData.newDate,
          newTime: formData.newTime,
          reason: formData.reason
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      alert('Booking rescheduled! Email sent to client.');
      closeModal();
      fetchBookings();
    } catch (error) {
      console.error('Error rescheduling booking:', error);
      alert('Failed to reschedule booking');
    }
  };

  const handleCancel = async () => {
    if (!formData.reason) {
      alert('Please provide a cancellation reason');
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      
      await axios.post(
        `/api/admin/bookings/${selectedBooking._id}/cancel`,
        { reason: formData.reason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      alert('Booking cancelled! Email sent to client.');
      closeModal();
      fetchBookings();
    } catch (error) {
      console.error('Error cancelling booking:', error);
      alert('Failed to cancel booking');
    }
  };

  const handleComplete = async (bookingId) => {
    if (!window.confirm('Mark this booking as completed?')) return;

    try {
      const token = localStorage.getItem('adminToken');
      
      await axios.post(
        `/api/admin/bookings/${bookingId}/complete`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      alert('Booking marked as completed!');
      fetchBookings();
    } catch (error) {
      console.error('Error completing booking:', error);
      alert('Failed to complete booking');
    }
  };

  const handleDelete = async (bookingId, bookingName) => {
    const confirmMessage = `⚠️ ATENÇÃO: Tem a certeza que deseja ELIMINAR permanentemente esta reserva?\n\nCliente: ${bookingName}\n\nEsta ação NÃO pode ser revertida!`;
    
    if (!window.confirm(confirmMessage)) return;

    // Double confirmation for safety
    if (!window.confirm('Confirma a eliminação? Esta ação é irreversível!')) return;

    try {
      const token = localStorage.getItem('adminToken');
      
      await axios.delete(
        `/api/admin/bookings/${bookingId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      alert('✅ Reserva eliminada com sucesso!');
      fetchBookings();
    } catch (error) {
      console.error('Error deleting booking:', error);
      alert('❌ Erro ao eliminar reserva');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { color: 'warning', label: 'Pendente', icon: '⏳' },
      confirmed: { color: 'success', label: 'Confirmado', icon: '✅' },
      cancelled: { color: 'danger', label: 'Cancelado', icon: '❌' },
      completed: { color: 'info', label: 'Concluído', icon: '🎉' },
      rescheduled: { color: 'warning', label: 'Reagendado', icon: '📅' }
    };

    const badge = badges[status] || badges.pending;
    return (
      <span className={`badge badge-${badge.color}`}>
        {badge.icon} {badge.label}
      </span>
    );
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('pt-PT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="admin-page">
        <div className="loading">
          <div className="spinner"></div>
          <p>A carregar reservas...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page admin-bookings">
      <div className="admin-header">
        <h1>📅 Gestão de Reservas</h1>
        <p>Veja e gira as suas marcações</p>
      </div>

      {/* Filter Tabs */}
      <div className="filter-tabs">
        <button
          className={`tab ${filter === 'all' ? 'active' : ''}`}
          onClick={() => setFilter('all')}
        >
          Todas ({bookings.length})
        </button>
        <button
          className={`tab ${filter === 'pending' ? 'active' : ''}`}
          onClick={() => setFilter('pending')}
        >
          ⏳ Pendentes
        </button>
        <button
          className={`tab ${filter === 'confirmed' ? 'active' : ''}`}
          onClick={() => setFilter('confirmed')}
        >
          ✅ Confirmadas
        </button>
        <button
          className={`tab ${filter === 'rescheduled' ? 'active' : ''}`}
          onClick={() => setFilter('rescheduled')}
        >
          📅 Reagendadas
        </button>
        <button
          className={`tab ${filter === 'completed' ? 'active' : ''}`}
          onClick={() => setFilter('completed')}
        >
          🎉 Concluídas
        </button>
        <button
          className={`tab ${filter === 'cancelled' ? 'active' : ''}`}
          onClick={() => setFilter('cancelled')}
        >
          ❌ Canceladas
        </button>
      </div>

      {/* Bookings List */}
      {bookings.length === 0 ? (
        <div className="empty-state">
          <p>📭 Nenhuma reserva encontrada</p>
        </div>
      ) : (
        <div className="bookings-grid">
          {bookings.map((booking) => (
            <div key={booking._id} className="booking-card">
              <div className="booking-header">
                <div className="booking-client">
                  <h3>{booking.name}</h3>
                  <p className="email">{booking.email}</p>
                  <p className="phone">{booking.phone}</p>
                </div>
                {getStatusBadge(booking.status)}
              </div>

              <div className="booking-details">
                <div className="detail-row">
                  <span className="label">🔮 Serviço:</span>
                  <span className="value">
                    {booking.serviceDetails?.name?.pt || booking.service}
                  </span>
                </div>

                <div className="detail-row">
                  <span className="label">📅 Data:</span>
                  <span className="value">{formatDate(booking.date)}</span>
                </div>

                <div className="detail-row">
                  <span className="label">⏰ Hora:</span>
                  <span className="value">{booking.time}</span>
                </div>

                <div className="detail-row">
                  <span className="label">⏱️ Duração:</span>
                  <span className="value">{booking.duration} min</span>
                </div>

                <div className="detail-row">
                  <span className="label">💰 Valor:</span>
                  <span className="value">
                    €{booking.serviceDetails?.price || booking.amount || 0}
                  </span>
                </div>

                {booking.message && (
                  <div className="detail-row">
                    <span className="label">💬 Mensagem:</span>
                    <span className="value message">{booking.message}</span>
                  </div>
                )}

                {booking.calendarSynced && (
                  <div className="calendar-sync">
                    ✅ Sincronizado com calendário
                  </div>
                )}
              </div>

              <div className="booking-actions">
                {booking.status === 'pending' && (
                  <>
                    <button
                      className="btn btn-success"
                      onClick={() => openModal('confirm', booking)}
                    >
                      ✅ Confirmar
                    </button>
                    <button
                      className="btn btn-warning"
                      onClick={() => openModal('reschedule', booking)}
                    >
                      📅 Reagendar
                    </button>
                    <button
                      className="btn btn-danger"
                      onClick={() => openModal('cancel', booking)}
                    >
                      ❌ Cancelar
                    </button>
                  </>
                )}

                {booking.status === 'confirmed' && (
                  <>
                    <button
                      className="btn btn-warning"
                      onClick={() => openModal('reschedule', booking)}
                    >
                      📅 Reagendar
                    </button>
                    <button
                      className="btn btn-success"
                      onClick={() => handleComplete(booking._id)}
                    >
                      🎉 Marcar Concluído
                    </button>
                    <button
                      className="btn btn-danger"
                      onClick={() => openModal('cancel', booking)}
                    >
                      ❌ Cancelar
                    </button>
                  </>
                )}

                {booking.status === 'rescheduled' && (
                  <>
                    <button
                      className="btn btn-success"
                      onClick={() => openModal('confirm', booking)}
                    >
                      ✅ Confirmar Nova Data
                    </button>
                    <button
                      className="btn btn-danger"
                      onClick={() => openModal('cancel', booking)}
                    >
                      ❌ Cancelar
                    </button>
                  </>
                )}

                {/* Delete button - available for all statuses */}
                <button
                  className="btn btn-delete"
                  onClick={() => handleDelete(booking._id, booking.name)}
                  title="Eliminar permanentemente esta reserva"
                >
                  🗑️ Eliminar
                </button>
              </div>

              <div className="booking-meta">
                <small>ID: {booking._id}</small>
                <small>Criado: {formatDate(booking.createdAt)}</small>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {showModal && selectedBooking && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                {modalType === 'confirm' && '✅ Confirmar Reserva'}
                {modalType === 'reschedule' && '📅 Reagendar Reserva'}
                {modalType === 'cancel' && '❌ Cancelar Reserva'}
              </h2>
              <button className="modal-close" onClick={closeModal}>×</button>
            </div>

            <div className="modal-body">
              {/* Client Info */}
              <div className="client-info">
                <h3>{selectedBooking.name}</h3>
                <p>{selectedBooking.email}</p>
                <p>{formatDate(selectedBooking.date)} às {selectedBooking.time}</p>
              </div>

              {/* Confirm Form */}
              {modalType === 'confirm' && (
                <>
                  <div className="form-group">
                    <label>Link da Reunião (opcional)</label>
                    <input
                      type="url"
                      className="form-control"
                      value={formData.meetingLink}
                      onChange={(e) => setFormData({ ...formData, meetingLink: e.target.value })}
                      placeholder="https://zoom.us/j/123456789"
                    />
                  </div>

                  <div className="form-group">
                    <label>Notas Admin (opcional)</label>
                    <textarea
                      className="form-control"
                      value={formData.adminNotes}
                      onChange={(e) => setFormData({ ...formData, adminNotes: e.target.value })}
                      rows="3"
                      placeholder="Notas internas..."
                    />
                  </div>
                </>
              )}

              {/* Reschedule Form */}
              {modalType === 'reschedule' && (
                <>
                  <div className="form-group">
                    <label>Nova Data *</label>
                    <input
                      type="date"
                      className="form-control"
                      value={formData.newDate}
                      onChange={(e) => setFormData({ ...formData, newDate: e.target.value })}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Nova Hora *</label>
                    <input
                      type="time"
                      className="form-control"
                      value={formData.newTime}
                      onChange={(e) => setFormData({ ...formData, newTime: e.target.value })}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Motivo (opcional)</label>
                    <textarea
                      className="form-control"
                      value={formData.reason}
                      onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                      rows="3"
                      placeholder="Explique o motivo do reagendamento..."
                    />
                  </div>
                </>
              )}

              {/* Cancel Form */}
              {modalType === 'cancel' && (
                <div className="form-group">
                  <label>Motivo do Cancelamento *</label>
                  <textarea
                    className="form-control"
                    value={formData.reason}
                    onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                    rows="4"
                    placeholder="Explique o motivo do cancelamento..."
                    required
                  />
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={closeModal}>
                Fechar
              </button>
              {modalType === 'confirm' && (
                <button className="btn btn-success" onClick={handleConfirm}>
                  ✅ Confirmar e Enviar Email
                </button>
              )}
              {modalType === 'reschedule' && (
                <button className="btn btn-warning" onClick={handleReschedule}>
                  📅 Reagendar e Enviar Email
                </button>
              )}
              {modalType === 'cancel' && (
                <button className="btn btn-danger" onClick={handleCancel}>
                  ❌ Cancelar e Enviar Email
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminBookings;
